import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, X } from 'lucide-react';
import type { Ticket } from '@/entities/ticket';
import { apiErrorText, massIncidentsApi } from '@/shared/api';
import { MassIncidentIcon } from '@/shared/config';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  EmptyState,
  ErrorState,
  IconButton,
  Scroller,
  Skeleton,
  StatusBadge,
  promptDialog,
  toast,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { incidentMeta } from '../model/logic';
import { refreshAfterIncidentChange, useMassIncidentUi } from '../model/store';
import { isLiveTicket, useIncident } from '../model/useIncident';
import styles from './MassIncidents.module.css';

export const MassIncidentPanel: React.FC<{ incidentId: string; onClose: () => void }> = ({
  incidentId,
  onClose,
}) => {
  const { summary, tickets, loading, error, reload } = useIncident(incidentId);
  const openReply = useMassIncidentUi((s) => s.openReply);
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const active = summary?.status === 'active';
  const live = tickets.filter(isLiveTicket);

  const run = async (key: string, fn: () => Promise<unknown>, affected: string[] = []) => {
    setBusy(key);
    try {
      await fn();
      await reload();
      refreshAfterIncidentChange(incidentId, affected);
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось изменить сбой'));
    } finally {
      setBusy(null);
    }
  };

  const rename = async () => {
    if (!summary) return;
    const title = await promptDialog({
      title: 'Название сбоя',
      description: 'Его видят заявители всех заявок сбоя.',
      label: 'Название',
      defaultValue: summary.title,
      confirmLabel: 'Сохранить',
    });
    if (title == null || !title.trim() || title.trim() === summary.title) return;
    void run('rename', () => massIncidentsApi.update(incidentId, { title: title.trim() }));
  };

  const remove = (t: Ticket) =>
    void run(`remove:${t.id}`, () => massIncidentsApi.unmark(t.id), [t.id]);

  const toggleResolved = () =>
    void run('status', () =>
      massIncidentsApi.update(incidentId, { status: active ? 'resolved' : 'active' }),
    );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={styles.wide} ref={popupRef} initialFocus={popupRef}>
        <div className={styles.head}>
          <span className={cn(styles.headIcon, !active && summary && styles.headIconResolved)}>
            <MassIncidentIcon size={18} aria-hidden />
          </span>
          <div className={styles.headText}>
            <DialogTitle className={styles.title}>
              {summary?.title ?? <Skeleton width="60%" height="1.25rem" />}
            </DialogTitle>
            <DialogDescription className={styles.meta}>
              {summary ? incidentMeta(summary) : <Skeleton width="40%" height="0.875rem" />}
            </DialogDescription>
          </div>
          {active && (
            <IconButton label="Переименовать сбой" size="sm" onClick={() => void rename()} disabled={busy !== null}>
              <Pencil size={14} aria-hidden />
            </IconButton>
          )}
        </div>

        {error ? (
          <ErrorState message={error} onRetry={() => void reload()} />
        ) : loading && tickets.length === 0 ? (
          <div className={styles.rows} aria-busy>
            {[0, 1, 2].map((i) => (
              <div key={i} className={styles.row}>
                <Skeleton width="3rem" height="0.875rem" />
                <Skeleton width="70%" height="0.875rem" />
                <Skeleton width="5rem" height="1.25rem" radius="999px" />
              </div>
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState compact title="В сбое нет заявок" hint="Все заявки убраны из сбоя." />
        ) : (
          <Scroller className={styles.rowsScroller} viewportClassName={styles.rows}>
            {tickets.map((t) => {
              const closed = !isLiveTicket(t);
              return (
                <div key={t.id} className={cn(styles.row, closed && styles.rowClosed)}>
                  <Link to={`/tickets/view/${t.id}`} className={styles.rowLink} onClick={onClose}>
                    <span className={styles.rowNumber}>{t.number != null ? `№${t.number}` : ''}</span>
                    <span className={styles.rowTitle}>{t.title}</span>
                    <span className={styles.rowPerson}>{t.creator?.displayName ?? ''}</span>
                  </Link>
                  <StatusBadge status={t.status} label={t.statusLabel} size="sm" />
                  {closed || !active ? (
                    <span className={styles.rowAction} aria-hidden />
                  ) : (
                    <IconButton
                      label="Убрать из сбоя"
                      tooltip="Убрать из сбоя"
                      tone="danger"
                      size="sm"
                      className={styles.rowAction}
                      disabled={busy !== null}
                      onClick={() => remove(t)}
                    >
                      <X size={14} aria-hidden />
                    </IconButton>
                  )}
                </div>
              );
            })}
          </Scroller>
        )}

        <DialogFooter className={styles.footer}>
          <Button
            variant="ghost"
            onClick={() => {
              onClose();
              navigate(`/tickets?mass=${incidentId}`);
            }}
          >
            Открыть списком
          </Button>
          <span className={styles.footerGap} />
          {summary && (
            <Button variant="secondary" onClick={toggleResolved} disabled={busy !== null}>
              {active ? 'Сбой устранён' : 'Вернуть в работу'}
            </Button>
          )}
          {active && live.length > 0 && (
            <Button onClick={() => openReply(incidentId)} disabled={busy !== null}>
              Ответить всем · {live.length}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
